import React from 'react'
import { Col, Row } from 'react-bootstrap'

import './LandingPage.scss'
import InputBar from './InputBar'
import { useTranslation } from 'react-i18next'
import imgMultilang from './images/multilang.png'
import imgMultilangWebp from './images/multilang.webp'
import imgLogin from './images/login.png'
import imgLoginWebp from './images/login.webp'
import imgMobileWebp from './images/mobile.webp'
import imgMobile from './images/mobile.png'
import imgDark from './images/dark.png'
import imgDarkWebp from './images/dark.webp'
import { useLayoutStore } from '../../../state/layout.store'
import classNames from 'classnames'
import Changelog from './Changelog'
import ImgWithFallback from '../../../components/ImgWithFallback'
import GoogleAd from '../../../components/GoogleAd'
import { useLoadAdsenseScript } from '../../../hooks/util/useExternalScript'
import AddPackageModal from '../../../components/Modals/AddPackageModal'

const LandingPage = () => {
  const { t } = useTranslation('homepage')
  useLoadAdsenseScript()

  return (
    <div className="landing-page">
      <Row className="mt-3 mb-3">
        <Col md={{ span: 8, offset: 2 }}>
          <div className="text-center mb-4">
            <h1 className="text-primary font-weight-light display-6">{t('title')}</h1>
            <h3 className="font-weight-light">
              {t('subtitle.begin')}{' '}
              <span className="text-muted">
                {t('subtitle.end', {
                  ns: 'homepage',
                  carriers: 'China, Amazon, eBay, Wish, AliExpress, Royal Mail, Deutsche Post, UPS, FedEx, DHL',
                })}
              </span>
            </h3>
          </div>
          <div>
            <InputBar />
            <AddPackageModal />
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="mt-3 text-center" align="center">
            <GoogleAd
              client="ca-pub-3952596231252607"
              slot="9751293244"
              styles={{
                display: 'block',
              }}
              className="ad_below_inputbar"
              autoFormat={false}
              data-full-width-responsive="false"
            />
          </div>
        </Col>
      </Row>

      <hr />

      <Features />

      <hr />

      <Row>
        <Col>
          <div className="text-center mb-3">
            <h1 className="text-primary font-weight-light display-6">{t('changelog.title')}</h1>
          </div>
          <Changelog />
        </Col>
      </Row>

      <hr />

      <Row className="mb-3">
        <Col>
          <h1 className="text-primary text-center display-6">{t('supported carriers.begin')}</h1>
        </Col>
      </Row>
      <Row>
        <Col md={3} className="mb-3">
          <div className="carrier-card">
            <img src="https://carriers-api.geartrack.app/static/ups/ups.png" alt="UPS" width={240} height={120} />
          </div>
        </Col>
        <Col md={3} className="mb-3">
          <div className="carrier-card">
            <img
              src="https://carriers-api.geartrack.app/static/correos/correos.png"
              alt="Correos"
              width={240}
              height={120}
            />
          </div>
        </Col>
        <Col md={3} className="mb-3">
          <div className="carrier-card">
            <img src="https://carriers-api.geartrack.app/static/dhl_es/dhl_es.png" alt="DHL" width={240} height={120} />
          </div>
        </Col>
        <Col md={3} className="mb-3">
          <div className="carrier-card">
            <img src="https://carriers-api.geartrack.app/static/fedex/fedex.png" alt="FedEx" width={240} height={120} />
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mb-3">
          <div className="carrier-card">
            <img src="https://carriers-api.geartrack.app/static/gls_es/gls_es.png" alt="GLS" width={240} height={120} />
          </div>
        </Col>
        <Col md={4} className="mb-3">
          <div className="carrier-card">
            <img src="https://carriers-api.geartrack.app/static/nacex/nacex.png" alt="Nacex" width={240} height={120} />
          </div>
        </Col>
        <Col md={4} className="mb-3">
          <div className="carrier-card">
            <img
              src="https://carriers-api.geartrack.app/static/royalmail/royalmail.png"
              alt="Royal Mail"
              width={240}
              height={120}
            />
          </div>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col>
          <h2 className="text-center text-muted" style={{ fontWeight: 400 }}>
            {t('supported carriers.end')}
          </h2>
        </Col>
      </Row>
    </div>
  )
}

const Features = () => {
  const { t } = useTranslation('homepage')
  const isDarkMode = useLayoutStore(state => state.darkMode)

  return (
    <>
      <Row className="mb-3">
        <Col>
          <h1 className="text-primary text-center display-6">{t('features.features')}</h1>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md={6}>
          <Row>
            <Col md={12} className="mb-3">
              <div
                className={classNames(
                  {
                    'bg-white': !isDarkMode,
                    'bg-dark': isDarkMode,
                  },
                  'rounded border p-4'
                )}
              >
                <h2 className="text-center">{t('features.mobile first.name')}</h2>

                <div className="text-center">
                  <ImgWithFallback
                    src={imgMobileWebp}
                    fallback={imgMobile}
                    className="img-fluid shadow-sm"
                    alt="Responsive Design"
                    width="300"
                    height="516"
                  />
                </div>

                <ul className="w-75 mx-auto mt-3 lh-lg list-unstyled">
                  <li>{t('features.mobile first.desc')}</li>
                </ul>
              </div>
            </Col>

            <Col md={12} className="mb-3">
              <div className="rounded border p-4 bg-dark">
                <h2 className="text-center text-white">{t('features.dark mode.name')}</h2>

                <div className="text-center">
                  <ImgWithFallback
                    src={imgDarkWebp}
                    fallback={imgDark}
                    className="img-fluid shadow-sm"
                    alt="Dark Mode"
                    width="300"
                    height="174"
                  />
                </div>

                <ul className="w-75 mx-auto mt-3 lh-lg list-unstyled text-white">
                  <li>{t('features.dark mode.desc')}</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Col>
        <Col md={6}>
          <Row className="align-items-stretch">
            <Col md={12} className="mb-3">
              <div
                className={classNames(
                  {
                    'bg-white': !isDarkMode,
                    'bg-dark': isDarkMode,
                  },
                  'rounded border p-4'
                )}
              >
                <h2 className="text-center">{t('features.login.name')}</h2>

                <div className="text-center">
                  <ImgWithFallback
                    src={imgLoginWebp}
                    fallback={imgLogin}
                    className="img-fluid shadow-sm"
                    alt="Login providers"
                    width="300"
                    height="276"
                  />
                </div>

                <ul className="w-75 mx-auto mt-3 lh-lg list-unstyled">
                  <li>
                    {t('features.login.desc1')}
                    <ul>
                      <li dangerouslySetInnerHTML={{ __html: t('features.login.desc2') }} />
                    </ul>
                  </li>
                  <li>{t('features.login.desc3')}</li>
                </ul>
              </div>
            </Col>
            <Col md={12}>
              <div
                className={classNames(
                  {
                    'bg-white': !isDarkMode,
                    'bg-dark': isDarkMode,
                  },
                  'rounded border p-4'
                )}
              >
                <h2 className="text-center">{t('features.multilang.name')}</h2>

                <div className="text-center">
                  <ImgWithFallback
                    src={imgMultilangWebp}
                    fallback={imgMultilang}
                    className="img-fluid shadow-sm"
                    alt="Multi Language"
                    width="300"
                    height="171"
                  />
                </div>

                <ul className="w-75 mx-auto mt-3 lh-lg list-unstyled">
                  <li>
                    {t('features.multilang.desc1')}
                    <ul>
                      <li>{t('features.multilang.desc2')}</li>
                    </ul>
                  </li>
                  <li>
                    {t('features.multilang.desc3')}
                    <ul>
                      <li>{t('features.multilang.desc4')}</li>
                    </ul>
                  </li>
                </ul>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  )
}

export default LandingPage
